// node_modules
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import React, { useContext, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useMobile } from "../../../hooks/useMobile";

// redux
import { setLoading } from "../../../redux/slices/app.slice";
import {
  useDeleteUserMutation,
  useLazyGetDeletedUserListQuery,
  useLazyGetUserListQuery,
  usePermanentlyDeleteUserMutation,
  useRecoverUserMutation,
  useUpdateUserStateMutation,
} from "../../../redux/services/user";
import { useGetOrganizationListQuery } from "../../../redux/services/organization";

// primereact components
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";

// context
import { ToastContext } from "../../../App";

// styles
import * as styles from "./UserManagement.module.css";

const UserManagement = () => {
  const showToast = useContext(ToastContext);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isMobile } = useMobile();

  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [id, setId] = useState(null);
  const [selectedName, setSelectedName] = useState(null);
  const [deleteMode, setDeleteMode] = useState("delete");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [data, setData] = useState([]);

  const {
    data: organizations,
    isSuccess: isGetOrganizationsSuccess,
    isError: isGetOrganizationError,
  } = useGetOrganizationListQuery();

  const [
    getUsers,
    {
      data: users,
      isSuccess: isGetUsersSuccess,
      isLoading: isGetUsersLoading,
      isError: isGetUsersError,
    },
  ] = useLazyGetUserListQuery();
  const [
    deleteUser,
    {
      isSuccess: isDeleteUserSuccess,
      isError: isDeleteUserError,
      error: deleteUserError,
    },
  ] = useDeleteUserMutation();
  const [
    updateUserState,
    {
      isSuccess: isUpdateUserStateSuccess,
      isError: isUpdateUserStateError,
      error: updateUserStateError,
    },
  ] = useUpdateUserStateMutation();
  const [
    getDeletedUsers,
    {
      data: deletedUserList,
      isSuccess: isGetDeletedUsersSuccess,
      isError: isGetDeletedUsersError,
    },
  ] = useLazyGetDeletedUserListQuery();
  const [
    recoverUser,
    { isSuccess: isRecoverUserSuccess, isError: isRecoverUserError },
  ] = useRecoverUserMutation();
  const [
    permanentlyDeleteUser,
    {
      isSuccess: isPermanentlyDeleteUserSuccess,
      isError: isPermanentlyDeleteUserError,
    },
  ] = usePermanentlyDeleteUserMutation();

  const goCreate = () => {
    if (!selectedOrganization) return;
    navigate(`create?org=${selectedOrganization.subdomain}`);
  };
  const goEdit = (rowData) =>
    navigate(`${rowData.id}/edit?org=${selectedOrganization.subdomain}`);
  const goPassword = (rowData) =>
    navigate(`${rowData.id}/password?org=${selectedOrganization.subdomain}`);

  const openDeleteModal = (rowData) => () => {
    setId(rowData.id);
    setSelectedName(rowData.name);
    setDeleteMode(rowData.isDeleted ? "permanently" : "delete");
    setShowDeleteModal(true);
  };

  useEffect(() => {
    if (selectedOrganization) {
      getUsers(selectedOrganization.subdomain);
      getDeletedUsers(selectedOrganization.subdomain);
    }
  }, [selectedOrganization]);

  useEffect(() => {
    if (isGetOrganizationsSuccess) {
      dispatch(setLoading(false));
    }
  }, [isGetOrganizationsSuccess]);

  useEffect(() => {
    if (isGetOrganizationError) {
      showToast("error", "Organization", "Unable to load organizations");
      dispatch(setLoading(false));
    }
  }, [isGetOrganizationError]);

  useEffect(() => {
    if (isGetUsersSuccess) {
      dispatch(setLoading(false));

      setData((prev) => [...prev, ...users]);

      getDeletedUsers(selectedOrganization.subdomain);
    }
  }, [isGetUsersSuccess]);

  useEffect(() => {
    if (isGetUsersError) {
      showToast("error", "User", "Unable to load users");
      dispatch(setLoading(false));
    }
  }, [isGetUsersError]);

  useEffect(() => {
    let tempData = [];
    if (isGetUsersSuccess) tempData = users;
    if (isGetDeletedUsersSuccess) tempData = [...tempData, ...deletedUserList];

    setData(tempData);
  }, [isGetUsersSuccess, users, isGetDeletedUsersSuccess, deletedUserList]);

  useEffect(() => {
    if (isDeleteUserSuccess) {
      showToast("success", "User", "User deleted!");
      setShowDeleteModal(false);
      getDeletedUsers(selectedOrganization.subdomain);
    }
  }, [isDeleteUserSuccess]);

  useEffect(() => {
    if (isDeleteUserError) {
      showToast(
        "error",
        deleteUserError?.status,
        deleteUserError?.data?.message ||
          (deleteUserError?.data?.errors && "Incorrect data"),
      );
    }
  }, [isDeleteUserError]);

  useEffect(() => {
    if (isPermanentlyDeleteUserSuccess) {
      showToast("success", "User", "User deleted!");
      setShowDeleteModal(false);
      getDeletedUsers(selectedOrganization.subdomain);
    }
  }, [isPermanentlyDeleteUserSuccess]);

  useEffect(() => {
    if (isPermanentlyDeleteUserError) {
      showToast("error", "User", "Unable to permanently delete user");
    }
  }, [isPermanentlyDeleteUserError]);

  useEffect(() => {
    if (isRecoverUserSuccess) {
      showToast("success", "User", "User recovered!");
      setShowDeleteModal(false);
      getUsers(selectedOrganization.subdomain);
      getDeletedUsers(selectedOrganization.subdomain);
    }
  }, [isRecoverUserSuccess]);

  useEffect(() => {
    if (isRecoverUserError) {
      showToast("error", "User", "Unable to recover user");
    }
  }, [isRecoverUserError]);

  useEffect(() => {
    if (isUpdateUserStateSuccess) {
      showToast("success", "User", "User updated!");
    }
  }, [isUpdateUserStateSuccess]);

  useEffect(() => {
    if (isUpdateUserStateError) {
      showToast(
        "error",
        updateUserStateError?.status,
        updateUserStateError?.data?.message ||
          (updateUserStateError?.data?.errors && "Incorrect data"),
      );
    }
  }, [isUpdateUserStateError]);

  const onPermanentlyDelete = () => {
    permanentlyDeleteUser({ id, subdomain: selectedOrganization.subdomain });
  };

  const onDelete = () => {
    deleteUser({ id, subdomain: selectedOrganization.subdomain });
  };

  const actionBodyTemplate = (rowData) => {
    return (
      <div className={styles.cellSoundWrapper}>
        {rowData.isDeleted ? (
          <Button
            icon="pi pi-refresh"
            className={`p-button-text ${styles.soundBtn}`}
            onClick={() =>
              recoverUser({
                id: rowData.id,
                subdomain: selectedOrganization.subdomain,
              })
            }
          />
        ) : (
          <Button
            icon="pi pi-pencil"
            className={`p-button-text ${styles.soundBtn}`}
            onClick={() => goEdit(rowData)}
          />
        )}
        <Button
          icon="pi pi-key"
          disabled={rowData.isDeleted}
          className={`p-button-text ${styles.soundBtn}`}
          onClick={() => goPassword(rowData)}
        />
        <Button
          icon="pi pi-trash"
          className={`p-button-text ${styles.deleteBtn}`}
          onClick={openDeleteModal(rowData)}
        />
      </div>
    );
  };

  return (
    <div className={styles.pageWrapper}>
      <div className={`${styles.header}`}>
        {isGetOrganizationsSuccess && (
          <Dropdown
            value={selectedOrganization}
            onChange={(e) => setSelectedOrganization(e.value)}
            options={organizations?.toSorted((a, b) =>
              a.name.localeCompare(b.name),
            )}
            optionLabel="name"
            placeholder="Select organization"
            className={`${styles.dropdown}`}
          />
        )}
        <div className={styles.titleRow}>
          <div className={styles.headerTitle}>
            <h2>Users</h2>
            {selectedOrganization && (
              <span className={styles.organizationLabel}>
                {selectedOrganization.name}
              </span>
            )}
          </div>
          {selectedOrganization && (
            <Button className={styles.createBtn} onClick={goCreate}>
              <span>ADD USER</span> <i className="pi pi-plus" />
            </Button>
          )}
        </div>
      </div>
      {selectedOrganization ? (
        <>
          {users &&
            (isMobile ? (
              <div className={styles.cardList}>
                {data.map((user) => (
                  <div key={user.email} className={styles.cardItem}>
                    <div className={styles.cardInfo}>
                      <div className={styles.name}>{user.name}</div>
                      <div className={styles.subfield}>{user.email}</div>
                      <div className={styles.subfield}>{user.phone}</div>
                      <div className={`${styles.subfield} uppercase`}>
                        {user.role}
                      </div>
                    </div>
                    <div className={styles.actions}>
                      {user.active ? (
                        <button
                          className={styles.cardEditBtn}
                          disabled={user.isDeleted}
                          onClick={() => {
                            updateUserState({
                              id: user.id,
                              payload: {
                                active: false,
                              },
                              subdomain: selectedOrganization.subdomain,
                            });
                          }}
                        >
                          <i className="pi pi-eye" />
                        </button>
                      ) : (
                        <button
                          className={styles.cardEditBtn}
                          disabled={user.isDeleted}
                          onClick={() => {
                            updateUserState({
                              id: user.id,
                              payload: {
                                active: true,
                              },
                              subdomain: selectedOrganization.subdomain,
                            });
                          }}
                        >
                          <i className="pi pi-eye-slash" />
                        </button>
                      )}
                      {user.isDeleted ? (
                        <button
                          className={styles.cardEditBtn}
                          onClick={() =>
                            recoverUser({
                              id: user.id,
                              subdomain: selectedOrganization.subdomain,
                            })
                          }
                        >
                          <i className="pi pi-refresh" />
                        </button>
                      ) : (
                        <button
                          className={styles.cardEditBtn}
                          onClick={() => goEdit(user)}
                        >
                          <i className="pi pi-pencil" />
                        </button>
                      )}
                      <button
                        className={styles.cardEditBtn}
                        onClick={() => goPassword(user)}
                        disabled={user.isDeleted}
                      >
                        <i className="pi pi-key" />
                      </button>
                      <button
                        className={styles.cardDeleteBtn}
                        onClick={openDeleteModal(user)}
                      >
                        <i className="pi pi-trash" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <DataTable value={data} rows={6} loading={isGetUsersLoading}>
                <Column
                  field="name"
                  header="Name"
                  body={(rowData) => {
                    return (
                      <div className={styles.cellInsider}>{rowData.name}</div>
                    );
                  }}
                />

                <Column
                  field="role"
                  header="Role"
                  body={(rowData) => {
                    return (
                      <div className={styles.cellInsider}>
                        {rowData.role.slice(0, 1).toUpperCase() +
                          rowData.role.slice(1)}
                      </div>
                    );
                  }}
                />

                <Column
                  field="email"
                  header={() => {
                    return <div>Email</div>;
                  }}
                  body={(rowData) => {
                    return (
                      <div className={styles.cellInsider}>{rowData.email}</div>
                    );
                  }}
                />

                <Column
                  field="phone"
                  header={() => {
                    return <div>Phone</div>;
                  }}
                  body={(rowData) => {
                    return (
                      <div className={styles.cellInsider}>{rowData.phone}</div>
                    );
                  }}
                />

                <Column
                  field="active"
                  header={() => {
                    return <div>Active</div>;
                  }}
                  body={(rowData) => {
                    return (
                      <div>
                        {rowData.active ? (
                          <Button
                            icon="pi pi-eye"
                            disabled={rowData.isDeleted}
                            className={`p-button-text ${styles.activeBtn}`}
                            onMouseUp={() => {
                              updateUserState({
                                id: rowData.id,
                                payload: {
                                  active: false,
                                },
                                subdomain: selectedOrganization.subdomain,
                              });
                            }}
                          />
                        ) : (
                          <Button
                            icon="pi pi-eye-slash"
                            disabled={rowData.isDeleted}
                            className={`p-button-text ${styles.activeBtn}`}
                            onMouseUp={() => {
                              updateUserState({
                                id: rowData.id,
                                payload: {
                                  active: true,
                                },
                                subdomain: selectedOrganization.subdomain,
                              });
                            }}
                          />
                        )}
                      </div>
                    );
                  }}
                />

                <Column
                  body={actionBodyTemplate}
                  header="Actions"
                  style={{ width: "200px" }}
                />
              </DataTable>
            ))}
        </>
      ) : (
        <div>Please select organization</div>
      )}

      <Dialog
        closable={false}
        header={
          <div style={{ width: "100%", textAlign: "center" }}>
            Are You Sure?
          </div>
        }
        visible={showDeleteModal}
        blockScroll
        onHide={() => {
          setShowDeleteModal(false);
        }}
        footer={() => (
          <div className="flex flex-column gap-2 justify-content-end">
            <Button
              label="Delete"
              className={`p-button-success ${styles.deleteDialogBtn}`}
              onClick={
                deleteMode === "permanently" ? onPermanentlyDelete : onDelete
              }
            />
            <Button
              label="Cancel"
              text
              className={`p-button-success ${styles.cancelDialogBtn}`}
              onClick={() => {
                setShowDeleteModal(false);
              }}
            />
          </div>
        )}
        className={`p-fluid ${styles.deleteModal}`}
      >
        <div className={styles.text}>
          <span>
            You want to {deleteMode === "permanently" ? "permanently" : ""}{" "}
            delete user: <strong>{selectedName}</strong>.
          </span>
          Confirm this action by pressing ‘Delete’ or cancel it.
        </div>
      </Dialog>
    </div>
  );
};

export default UserManagement;
